// /opt/isp_erp/frontend/src/pages/warehouse/VinetasBodega.jsx
import React, { useState, useEffect } from "react";
import {
  Box, Typography, Paper, Table, TableHead, TableRow, TableCell, TableBody, TableContainer, TablePagination,
  Button, TextField, MenuItem, Alert, Chip, CircularProgress,
} from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import LocalOfferOutlined from "@mui/icons-material/LocalOfferOutlined";
import axios from "../../api/axios";
import { mensajeError } from "../../utils/mensajesError";
import { cargarContextoBodega } from "../../utils/contextoBodega";
import { temaSucursales } from "./sucursalesTema";
import AsignarTecnicoSucursalDialog from "../../components/warehouse/AsignarTecnicoSucursalDialog";

const API = "/warehouse/sucursales-equipos";
const ESTADO_COLOR = { BODEGA: "success", EN_ENVIO: "warning", EN_SUCURSAL: "secondary", ASIGNADO: "info", USADO: "default" };
const ESTADO_TEXTO = { BODEGA: "En bodega central", EN_ENVIO: "En tránsito", EN_SUCURSAL: "En sucursal", ASIGNADO: "Con técnico", USADO: "Usada" };

function useDebounced(valor, ms) {
  const [v, setV] = useState(valor);
  useEffect(() => {
    const t = setTimeout(() => setV(valor), ms);
    return () => clearTimeout(t);
  }, [valor, ms]);
  return v;
}

export default function VinetasBodega() {
  const [ctx, setCtx] = useState(null);
  const [ctxListo, setCtxListo] = useState(false);
  const [ubicacion, setUbicacion] = useState("");
  const [estado, setEstado] = useState("");
  const [texto, setTexto] = useState("");
  const q = useDebounced(texto.trim(), 350);
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState(25);
  const [data, setData] = useState({ items: [], total: 0 });
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState("");
  const [aviso, setAviso] = useState("");
  const [version, setVersion] = useState(0);
  const [asignando, setAsignando] = useState(null);

  useEffect(() => {
    cargarContextoBodega().then((c) => { setCtx(c); setCtxListo(true); });
  }, []);

  useEffect(() => { setPage(0); }, [q, estado, ubicacion]);

  useEffect(() => {
    if (!ctx) return undefined;
    let vivo = true;
    setCargando(true);
    setError("");
    axios.get(`${API}/vinetas-lista`, { params: { q: q || undefined, estado: estado || undefined, ubicacion: ubicacion || undefined, page: page + 1, page_size: rows } })
      .then((r) => { if (vivo) setData(r.data); })
      .catch((e) => { if (vivo) { setData({ items: [], total: 0 }); setError(mensajeError(e, "No se pudieron cargar las viñetas")); } })
      .finally(() => { if (vivo) setCargando(false); });
    return () => { vivo = false; };
  }, [ctx, q, estado, ubicacion, page, rows, version]);

  const puedeOperar = (v) => {
    if (!ctx || v.estado !== "EN_SUCURSAL" || !v.sucursal_id) return false;
    const s = (ctx.sucursales || []).find((x) => x.id === v.sucursal_id);
    return !!(s && s.puede_operar);
  };

  const devolver = async (v) => {
    if (!window.confirm(`¿Devolver la viñeta ${v.serial} a la bodega central?`)) return;
    setError("");
    setAviso("");
    try {
      const r = await axios.post(`${API}/${v.sucursal_id}/devolver-central`, { vinetas: [v.id] });
      setAviso(r.data.mensaje);
      setVersion((n) => n + 1);
    } catch (e) {
      setError(mensajeError(e, "No se pudo devolver la viñeta"));
    }
  };

  const puedeRecibir = (x) => {
    if (!ctx || x.estado !== "ASIGNADO" || !x.sucursal_tecnico_id) return false;
    const s = (ctx.sucursales || []).find((y) => y.id === x.sucursal_tecnico_id);
    return !!(s && s.puede_operar);
  };

  const recibirDeTecnico = async (x) => {
    if (!window.confirm(`¿Recibir la viñeta ${x.serial} de ${x.tecnico_nombre || "el técnico"} en la sucursal?`)) return;
    setError("");
    setAviso("");
    try {
      const r = await axios.post(`${API}/recibir-vineta-de-tecnico`, { vignette_id: x.id });
      setAviso(r.data.mensaje);
      setVersion((n) => n + 1);
    } catch (err) {
      setError(mensajeError(err, "No se pudo recibir la viñeta"));
    }
  };

  const envoltura = (hijos) => (<ThemeProvider theme={temaSucursales}>{hijos}</ThemeProvider>);

  if (!ctxListo) return envoltura(<Box sx={{ p: 6, textAlign: "center" }}><CircularProgress /></Box>);
  if (!ctx) return envoltura(<Box sx={{ p: 3 }}><Alert severity="error">No tienes acceso a las viñetas de bodega.</Alert></Box>);

  const hayAcciones = data.items.some((x) => puedeOperar(x) || puedeRecibir(x));
  const subtitulo = ctx.central
    ? "Todas las viñetas de la bodega. Filtra por ubicación para ver las de cada sucursal y las que tienen sus técnicos."
    : "Las viñetas de tu sucursal, las que vienen en camino y las que tienen en mano tus técnicos.";

  return envoltura(
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      <Box sx={{ borderRadius: 4, p: { xs: 2.5, md: 3 }, mb: 3, color: "#fff", display: "flex", alignItems: "center", gap: 2,
                 background: "linear-gradient(135deg, #0e7490 0%, #0891b2 55%, #38bdf8 100%)" }}>
        <LocalOfferOutlined sx={{ fontSize: 42, opacity: 0.9 }} />
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.1 }}>Viñetas</Typography>
          <Typography sx={{ opacity: 0.92, mt: 0.5 }}>{subtitulo}</Typography>
        </Box>
      </Box>

      <Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid #e2e8f0", p: { xs: 1.5, md: 2.5 } }}>
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2, alignItems: "center" }}>
          <TextField size="small" label="Buscar por serial, nombre o técnico" value={texto} onChange={(e) => setTexto(e.target.value)} sx={{ minWidth: 300 }} />
          <TextField select size="small" label="Estado" value={estado} onChange={(e) => setEstado(e.target.value)} sx={{ minWidth: 180 }}>
            <MenuItem value="">Todos</MenuItem>
            {Object.keys(ESTADO_TEXTO).map((k) => (<MenuItem key={k} value={k}>{ESTADO_TEXTO[k]}</MenuItem>))}
          </TextField>
          {ctx.central && (ctx.sucursales || []).length > 0 && (
            <TextField select size="small" label="Ubicación" value={ubicacion} onChange={(e) => setUbicacion(e.target.value)} sx={{ minWidth: 220 }}>
              <MenuItem value="">Todas</MenuItem>
              <MenuItem value="central">Bodega central</MenuItem>
              {ctx.sucursales.map((s) => (<MenuItem key={s.id} value={String(s.id)}>{s.nombre}</MenuItem>))}
            </TextField>
          )}
        </Box>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {aviso && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setAviso("")}>{aviso}</Alert>}
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Serial</TableCell>
                <TableCell>Nombre</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Ubicación</TableCell>
                {hayAcciones && <TableCell align="right">Acciones</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {cargando && (<TableRow><TableCell colSpan={5} align="center"><CircularProgress size={20} /></TableCell></TableRow>)}
              {!cargando && data.items.length === 0 && (<TableRow><TableCell colSpan={5} align="center">No hay viñetas con estos filtros</TableCell></TableRow>)}
              {!cargando && data.items.map((v) => (
                <TableRow key={v.id} hover>
                  <TableCell><strong>{v.serial}</strong></TableCell>
                  <TableCell>{v.nombre}</TableCell>
                  <TableCell><Chip size="small" color={ESTADO_COLOR[v.estado] || "default"} label={ESTADO_TEXTO[v.estado] || v.estado} /></TableCell>
                  <TableCell>{v.ubicacion}</TableCell>
                  {hayAcciones && (
                    <TableCell align="right">
                      {puedeRecibir(v) && (<Button size="small" color="warning" onClick={() => recibirDeTecnico(v)}>Recibir del técnico</Button>)}
                      {puedeOperar(v) && (
                        <>
                          <Button size="small" onClick={() => setAsignando(v)}>Asignar a técnico</Button>
                        </>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination component="div" count={data.total} page={page} onPageChange={(e, p) => setPage(p)}
            rowsPerPage={rows} onRowsPerPageChange={(e) => { setRows(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50]} labelRowsPerPage="Filas" />
        </TableContainer>
      </Paper>

      {asignando && (
        <AsignarTecnicoSucursalDialog sucursalId={asignando.sucursal_id} tipo="vineta" item={asignando}
          onClose={() => setAsignando(null)}
          onHecho={(msg) => { setAsignando(null); setAviso(msg); setVersion((n) => n + 1); }} />
      )}
    </Box>
  );
}
